import apiClient from "../config/api";

const hazardReportService = {
  /**
   * Get all hazard reports with optional filters
   * @returns {Promise<Array>}
   */
  getHazardReports: async () => {
    const response = await apiClient.get("/HazardReport");
    return response.data;
  },

  getTrends: async (filters = {}) => {
    const params = { ...filters };
    // Remove individual plantId if plantIds is present
    if (params.plantIds && params.plantIds.length > 0) {
      delete params.plantId;
    }

    const response = await apiClient.get("/HazardReport/trends", { 
      params,
      paramsSerializer: params => {
        const searchParams = new URLSearchParams();
        Object.keys(params).forEach(key => {
          const val = params[key];
          if (val === null || val === undefined || val === '') return;
          if (Array.isArray(val)) {
            val.forEach(v => searchParams.append(key, v));
          } else {
            searchParams.append(key, val);
          }
        });
        return searchParams.toString();
      }
    });
    return response.data;
  },

  getStatistics: async (filters = {}) => {
    const params = { ...filters };
    if (params.plantIds && params.plantIds.length > 0) {
      delete params.plantId;
    }

    const response = await apiClient.get("/HazardReport/statistics", { 
      params,
      paramsSerializer: params => {
        const searchParams = new URLSearchParams();
        Object.keys(params).forEach(key => {
          const val = params[key];
          if (val === null || val === undefined || val === '') return;
          if (Array.isArray(val)) {
            val.forEach(v => searchParams.append(key, v));
          } else {
            searchParams.append(key, val);
          }
        });
        return searchParams.toString();
      }
    });
    return response.data;
  },

  /**
   * Get specific hazard report by ID
   * @param {number} id
   * @returns {Promise<object>}
   */
  getHazardReportById: async (id) => {
    const response = await apiClient.get(`/HazardReport/${id}`);
    return response.data;
  },

  /**
   * Create a new hazard report
   * @param {object} payload
   * @returns {Promise<object>}
   */
  createHazardReport: async (payload) => {
    const response = await apiClient.post("/HazardReport", payload);
    return response.data;
  },

  /**
   * Update an existing hazard report
   * @param {number} id
   * @param {object} payload
   * @returns {Promise<object>}
   */
  updateHazardReport: async (id, payload) => {
    const response = await apiClient.post(`/HazardReport/update/${id}`, payload);
    return response.data;
  },

  /**
   * Upload an attachment for a hazard report
   * @param {number} hazardId
   * @param {File} file
   * @returns {Promise<object>}
   */
  uploadAttachment: async (hazardId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("hazardId", hazardId);

    const response = await apiClient.post(
      `/HazardReport/${hazardId}/attachments`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },

  /**
   * Delete a hazard report
   * @param {number} id
   * @returns {Promise<object>}
   */
  deleteHazardReport: async (id) => {
    const response = await apiClient.post(`/HazardReport/delete/${id}`);
    return response.data;
  },

  /**
   * Escalate hazard report to CAPA
   * @param {number} id
   * @returns {Promise<object>}
   */
  escalateToCapa: async (id) => {
    const response = await apiClient.post(`/HazardReport/${id}/escalate-to-capa`);
    return response.data;
  },

  /**
   * Approve or reject a hazard report
   * @param {number} id
   * @param {string} status - 'Approved' or 'Rejected'
   * @param {string} remarks
   * @returns {Promise<object>}
   */
  approveHazardReport: async (id, status, remarks) => {
    const response = await apiClient.post(`/HazardReport/${id}/approval`, {
      approvalStatus: status,
      remarks,
    });
    return response.data;
  },

  /**
   * Get hazard report settings for the current tenant
   * @returns {Promise<object>}
   */
  getHazardSettings: async () => {
    const response = await apiClient.get("/HazardReport/settings");
    return response.data;
  },

  /**
   * Save hazard report settings
   * @param {object} settings 
   * @returns {Promise<object>}
   */
  saveHazardSettings: async (settings) => {
    const response = await apiClient.post("/HazardReport/settings", settings);
    return response.data;
  },
};

export default hazardReportService;
